import { useState } from "react";
import { Linking, ScrollView, Text, View } from "react-native";

import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { DocumentHead } from "@/components/ui/DocumentHead";
import { Screen } from "@/components/ui/Screen";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";

const DELETION_EMAIL =
  "mailto:privacy@trovie.io?subject=Delete%20my%20Trovie%20account&body=Please%20delete%20my%20Trovie%20account.%0A%0AAccount%20email%3A%0AShop%20name%20(if%20any)%3A";

export default function AccountDeletionScreen() {
  const [requestError, setRequestError] = useState(false);

  // The App Store listing links here, so it is often a cold launch. Nothing is fetched, so the
  // screen is interactive as soon as it renders.
  //
  // This is the fallback (TRO-115): signed-in users delete their account from Settings → Account,
  // as App Store Guideline 5.1.1(v) requires and the Privacy Policy names. This page serves people
  // who cannot sign in or have not installed the app.
  useMarkInteractive(true);

  const sendDeletionRequest = async () => {
    setRequestError(false);
    try {
      await Linking.openURL(DELETION_EMAIL);
    } catch {
      setRequestError(true);
    }
  };

  return (
    <Screen>
      <DocumentHead>
        <title>Delete your Trovie account</title>
        <meta
          name="description"
          content="Request deletion of your Trovie account and associated data."
        />
      </DocumentHead>
      <ScrollView
        role="main"
        className="w-full self-center"
        contentContainerClassName="gap-8 px-5 py-10"
        style={{ maxWidth: 680 }}
      >
        <View aria-hidden className="items-start">
          <Logo size={30} />
        </View>

        <View className="gap-3">
          <Text
            accessibilityRole="header"
            aria-level={1}
            className="font-title text-3xl text-foreground"
          >
            Delete your Trovie account
          </Text>
          <Text className="max-w-xl font-body text-[16px] leading-6 text-muted-foreground">
            If you can sign in, delete your account yourself in the app: open{" "}
            <Text className="font-label text-foreground">Settings → Account → Delete Account</Text>
            . It takes effect immediately and needs no reply from us.
          </Text>
          <Text className="max-w-xl font-body text-[16px] leading-6 text-muted-foreground">
            If you can’t sign in, send us a deletion request from the email address you use for
            Trovie. Include your shop name if you have one. We’ll reply to verify the request before
            processing it.
          </Text>
        </View>

        <View className="gap-4 rounded-xl border border-border bg-surface p-5">
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="font-title text-lg text-foreground"
          >
            Request account deletion
          </Text>
          <Button title="Email deletion request" onPress={() => void sendDeletionRequest()} />
          {requestError
            ? (
              <Text accessibilityRole="alert" className="font-body text-sm text-loss">
                We couldn’t open your email app. Send your request to privacy@trovie.io instead.
              </Text>
            )
            : null}
          <Text selectable className="font-body text-sm text-muted-foreground">
            Or email privacy@trovie.io with the subject “Delete my Trovie account.”
          </Text>
        </View>

        <View className="gap-3">
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="font-title text-xl text-foreground"
          >
            What happens next
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            After we verify your request, what we delete depends on your role.
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            Shop owner: We delete the shop’s identifying and operational data, including profile
            information, customer data, uploads, scans, and stored images, and remove every
            membership for that shop. Other members lose access. Retained transaction records are
            de-identified as described below.
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            Non-owner member: We remove only your membership. The shop’s data stays.
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            In either case, we delete your personal Trovie data and your Trovie and Clerk accounts
            only if no shop memberships remain. If another membership remains, your account stays
            active there.
          </Text>
        </View>

        <View className="gap-3 border-t border-border pt-6">
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="font-title text-xl text-foreground"
          >
            What we may retain
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            We retain de-identified transaction records for analytics. We may keep other information
            only for as long as required for legal, accounting, tax, security, fraud-prevention, or
            regulatory purposes, then delete or de-identify it.
          </Text>
        </View>

        <View className="gap-3 border-t border-border pt-6">
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="font-title text-xl text-foreground"
          >
            Google, Apple, and subscriptions
          </Text>
          <Text className="font-body text-[15px] leading-6 text-muted-foreground">
            Deleting your Trovie account does not delete your Google or Apple account. Revoking
            Google or Apple access does not delete your Trovie account. Cancel any App Store or
            Google Play subscription separately through that store.
          </Text>
        </View>
      </ScrollView>
    </Screen>
  );
}
